/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from "@emotion/react";
import BlockIcon from "@material-ui/icons/Block";
import CreditCardIcon from "@material-ui/icons/CreditCard";
import FavoriteBorderSharp from "@material-ui/icons/FavoriteBorderSharp";
import HomeOutlinedIcon from "@material-ui/icons/HomeOutlined";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import LockOpenIcon from "@material-ui/icons/LockOpen";
import "firebase/compat/auth";
import React from "react";
import { useDispatch, useSelector } from "react-redux";

import { AccountRegisterInfo } from "../../components/account/molecules/AccountRegisterInfo";
import BorderLoader from "../../components/atoms/loader/BorderLoader";
import defaultIconImg from "../../designs/images/icon_profile.svg";
import { useI18n } from "../../hooks/i18n/i18n";
import { useEmoBalance } from "../../hooks/paw/useEmoBalance";
import { authActions } from "../../modules/auth";
import { modalActions } from "../../modules/modal";
import type { Store } from "../../store";
import { Paw } from "../../styles";

import PawCharge from "containers/modal/pawCharge/PawCharge";
import { AccountCurrentPaw } from "features/AccountCurrentPaw";
import { AccountMenuItem } from "features/AccountMenuItem";
import { AccountMenuSection } from "features/AccountMenuSection";
import { LogoutIcon } from "features/icon/LogoutIcon";
import { PaymentIcon } from "features/icon/PaymentIcon";
import { PawChargeIcon } from "features/icon/PawChargeIcon";
import { PawUseIcon } from "features/icon/PawUseIcon";
import { ProfileIcon } from "features/icon/ProfileIcon";
import { TicketIcon } from "features/icon/TicketIcon";
import { BreadcrumbNavigation } from "ui/BreadcrumbNavigation";
import { hasEmailProviderData } from "utility/auth";
import { useSimpleFeedback } from "./useSimpleFeedback";

import type { FC } from "react";

export const Account: FC = () => {
  const { t } = useI18n();
  const classes = styles();
  const user = useSelector((state: Store) => state.auth.user);
  const emoBalance = useEmoBalance();

  const dispatch = useDispatch();
  const [isShownCopiedFeedback, showCopiedFeedback] = useSimpleFeedback();

  const handleCopySpwnId = () => {
    const textArea = document.createElement("textarea");
    textArea.value = user.uid;
    document.body.appendChild(textArea);
    textArea.select();
    document.execCommand("Copy");
    textArea.remove();
    showCopiedFeedback(2000);
  };

  const handlePawChargeModalToggle = () => {
    dispatch(modalActions.togglePawChargeModal({}));
  };

  const handleLogout = () => {
    dispatch(authActions.logout.started());
  };

  const userDisplayName =
    user.displayName === null || user.displayName === undefined
      ? t("account.notSetUserName")
      : user.displayName;
  const userEmail =
    user.email === null || user.email === undefined
      ? t("account.notSetEmail")
      : user.email;
  let userPhotoURL =
    user.photoURL === null || user.photoURL === undefined
      ? defaultIconImg
      : user.photoURL;
  if (userPhotoURL.includes("pbs.twimg.com")) {
    userPhotoURL = userPhotoURL.replace("_normal.", "_200x200.");
  } else if (userPhotoURL.includes("graph.facebook.com")) {
    userPhotoURL += "?width=200&height=200";
  }

  const pawBalance = (() => {
    if (emoBalance.paidValue !== undefined) {
      return <Paw>{emoBalance.paidValue}</Paw>;
    }

    if (user !== undefined && user !== null && user.emailVerified === false) {
      return (
        <React.Fragment>{t("account.emailUnauthenticated")}</React.Fragment>
      );
    }

    return <BorderLoader />;
  })();

  return (
    <div css={classes.root}>
      <BreadcrumbNavigation
        breadcrumbs={[
          { label: t("common.routes.home"), link: "/" },
          { label: t("common.routes.account"), link: null },
        ]}
      />

      <AccountRegisterInfo
        spwnId={user.uid}
        userPhotoURL={userPhotoURL}
        userDisplayName={userDisplayName}
        userEmail={userEmail}
        onCopySpwnId={handleCopySpwnId}
        isShowCopiedFeedback={isShownCopiedFeedback}
      />

      <AccountCurrentPaw
        pawBalance={pawBalance}
        onClick={handlePawChargeModalToggle}
      />

      <div css={classes.menuSections}>
        <AccountMenuSection heading={t("account.news.title")} isEmergency>
          <AccountMenuItem
            to="/account/notifications"
            icon={<InfoOutlinedIcon />}
            menuName={t("account.news.list")}
          />
        </AccountMenuSection>

        <AccountMenuSection heading={t("account.ticket.title")}>
          <AccountMenuItem
            to="/account/ticket"
            icon={<TicketIcon />}
            menuName={t("account.ticket.myTicket")}
          />

          <AccountMenuItem
            to="/account/ticket/vod"
            icon={<TicketIcon />}
            menuName={t("account.ticket.myLive")}
          />

          <AccountMenuItem
            to="/voucher"
            icon={<TicketIcon />}
            menuName={t("account.ticket.voucher")}
          />
        </AccountMenuSection>

        <AccountMenuSection heading={t("account.history.title")}>
          <AccountMenuItem
            to="/account/settlement/history"
            icon={<PaymentIcon />}
            menuName={t("account.history.settlementHistory")}
          />

          <AccountMenuItem
            to="/account/lottery"
            icon={<TicketIcon />}
            menuName={t("account.history.applicationHistory")}
          />

          <AccountMenuItem
            to="/account/paw-charge-history"
            icon={<PawChargeIcon />}
            menuName={t("account.history.topUpHistory")}
          />

          <AccountMenuItem
            to="/account/paw-use-history"
            icon={<PawUseIcon />}
            menuName={t("account.history.usageHistory")}
          />
        </AccountMenuSection>

        <AccountMenuSection heading={t("account.account.title")}>
          <AccountMenuItem
            to="/account/edit"
            icon={<ProfileIcon />}
            menuName={t("account.account.editProfileInfo")}
          />
          <AccountMenuItem
            to="/account/blocklist"
            icon={<BlockIcon />}
            menuName={t("account.account.blockedUsers")}
          />
          <AccountMenuItem
            to="/account/watchlist"
            icon={<FavoriteBorderSharp />}
            menuName={t("account.account.watchlist")}
          />
          <AccountMenuItem
            to="/account/credit"
            icon={<CreditCardIcon />}
            menuName={t("account.account.creditCardInfo")}
          />

          <AccountMenuItem
            to="/account/address"
            icon={<HomeOutlinedIcon />}
            menuName={t("account.account.editShippingAddress")}
          />

          {0 < Object.keys(user).length && hasEmailProviderData(user) && (
            <AccountMenuItem
              to="/account/edit/password"
              icon={<LockOpenIcon />}
              menuName={t("account.editPassword")}
            />
          )}

          <AccountMenuItem
            to="/"
            icon={<LogoutIcon />}
            menuName={t("account.account.logout")}
            onClick={handleLogout}
          />
        </AccountMenuSection>
      </div>

      <PawCharge />
    </div>
  );
};

const styles = () => ({
  root: css`
    padding-bottom: 60px;
    background-color: #fff;
    @media screen and (min-width: 768px) {
      padding-bottom: 160px;
    }
  `,
  menuSections: css`
    display: grid;
    gap: 40px;
    width: 100%;
    max-width: 740px;
    margin: 40px auto 0;
  `,
});
